import React from 'react';
import { qualityProcess } from '../mock';
import {
  Mountain,
  Layers,
  Filter,
  CircleDot,
  FilterX,
  Sun,
  Atom,
  Cog,
  ClipboardCheck,
  Package,
} from 'lucide-react';
import { Reveal, StaggerGroup, StaggerItem } from './Reveal';
import { motion } from 'framer-motion';

const iconMap = { Mountain, Layers, Filter, CircleDot, FilterX, Sun, Atom, Cog, ClipboardCheck, Package };

const Quality = () => {
  return (
    <section id="quality" className="relative py-24 lg:py-32 bg-[#fafaf7] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        <div className="relative bg-gradient-to-br from-[#0a1f4a] via-[#0f2c6b] to-[#0a1f4a] p-10 lg:p-16 overflow-hidden">
          {/* Decorative rings */}
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full border border-[#c4a24a]/10" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-white/5" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full border border-[#c4a24a]/10" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full border border-white/5" />

          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />

          <div className="relative">
            <Reveal>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-[1px] bg-[#c4a24a]" />
                <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
                  04 — The Process
                </span>
              </div>
              <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
                <h2
                  className="lg:col-span-7 text-[42px] lg:text-[60px] xl:text-[68px] leading-[1.03] font-medium text-white text-balance"
                  style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.03em' }}
                >
                  From source <span className="italic text-[#c4a24a] font-light">to sip.</span>
                </h2>
                <p className="lg:col-span-5 text-[14.5px] leading-[1.8] text-white/70 font-light">
                  Every drop passes through a rigorous 10-stage purification and quality process — uncompromising, transparent, and world-class.
                </p>
              </div>
            </Reveal>

            {/* Process flow with connecting line */}
            <div className="relative">
              {/* Horizontal line for desktop - row 1 */}
              <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-[#c4a24a]/30 to-transparent" />

              <StaggerGroup className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4 mb-12 lg:mb-16" delay={0.1}>
                {qualityProcess.slice(0, 5).map((step, idx) => {
                  const Icon = iconMap[step.icon];
                  return (
                    <StaggerItem key={idx}>
                      <div className="flex flex-col items-center text-center group cursor-default">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-[#c4a24a]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <div className="relative w-20 h-20 rounded-full bg-white/[0.06] backdrop-blur-sm border border-white/15 flex items-center justify-center text-white group-hover:border-[#c4a24a] group-hover:bg-white/10 group-hover:scale-110 transition-all duration-500">
                            <Icon size={30} strokeWidth={1.3} />
                          </div>
                          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#c4a24a] flex items-center justify-center text-[9px] font-bold text-[#0a1f4a]">
                            {String(idx + 1).padStart(2, '0')}
                          </div>
                        </div>
                        <p className="mt-4 text-[11.5px] font-medium text-white leading-[1.4] whitespace-pre-line tracking-wide">
                          {step.label}
                        </p>
                      </div>
                    </StaggerItem>
                  );
                })}
              </StaggerGroup>

              {/* Horizontal line for desktop - row 2 */}
              <div className="hidden lg:block absolute top-[calc(50%+20px)] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-[#c4a24a]/30 to-transparent" />

              <StaggerGroup className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4" delay={0.1}>
                {qualityProcess.slice(5, 10).map((step, idx) => {
                  const Icon = iconMap[step.icon];
                  return (
                    <StaggerItem key={idx}>
                      <div className="flex flex-col items-center text-center group cursor-default">
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-[#c4a24a]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <div className="relative w-20 h-20 rounded-full bg-white/[0.06] backdrop-blur-sm border border-white/15 flex items-center justify-center text-white group-hover:border-[#c4a24a] group-hover:bg-white/10 group-hover:scale-110 transition-all duration-500">
                            <Icon size={30} strokeWidth={1.3} />
                          </div>
                          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#c4a24a] flex items-center justify-center text-[9px] font-bold text-[#0a1f4a]">
                            {String(idx + 6).padStart(2, '0')}
                          </div>
                        </div>
                        <p className="mt-4 text-[11.5px] font-medium text-white leading-[1.4] whitespace-pre-line tracking-wide">
                          {step.label}
                        </p>
                      </div>
                    </StaggerItem>
                  );
                })}
              </StaggerGroup>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quality;
